/* Finding one client in the Admin list.
 *
 * The rows are assembled from the Clerk organization list, so they arrive in
 * Clerk's order — creation date — and no ORDER BY in the D1 query can change
 * that. Ordering therefore has to happen after the merge, here.
 *
 * Search matches the account ids as well as the name because the id is often
 * the only thing to hand: a Google Ads customer number pasted out of a support
 * thread should find the client without anyone knowing what it is called. */

export interface SearchableClient {
  name: string;
  contentManagerId?: string | null;
  bqTable?: string | null;
  googleAdsCustomerId?: string | null;
  ga4PropertyId?: string | null;
  gscSiteUrl?: string | null;
  gatherupClientId?: string | null;
}

/* `numeric` keeps "Site 2" ahead of "Site 10" instead of sorting them as text.
   `sensitivity: 'base'` folds case and accents together, so a lowercase name
   does not land in a second alphabet after every capitalised one. */
export function sortClientsByName<T extends { name: string }>(clients: readonly T[]): T[] {
  return [...clients].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
  );
}

/* Sorted A-Z, then narrowed to the query. Terms are ANDed so that typing a
   second word refines the list — the opposite behaviour, where each extra word
   pulls in more rows, makes a search box useless at exactly the point you need
   it. An empty or whitespace-only query is not a filter. */
export function findClients<T extends SearchableClient>(
  clients: readonly T[],
  query: string,
  managerName: (id: string | null | undefined) => string | null | undefined = () => null,
): T[] {
  const sorted = sortClientsByName(clients);
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return sorted;

  return sorted.filter((c) => {
    const hay = [
      c.name,
      managerName(c.contentManagerId),
      c.bqTable,
      c.googleAdsCustomerId,
      c.ga4PropertyId,
      c.gscSiteUrl,
      c.gatherupClientId,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return terms.every((t) => hay.includes(t));
  });
}
